import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageTransition } from '../components/ui/PageTransition';
import { SEO } from '../components/shared/SEO';
import { AnimatedSection } from '../components/ui/AnimatedSection';
import { Button } from '../components/ui/Button';
import { API_ENDPOINTS } from '../config/api';
import { Footer } from '../components/layout/Footer';
import { motion } from 'framer-motion';
import { useSharedElement } from '../contexts/SharedElementContext';
import { ArrowLeft } from 'lucide-react';
import { SharedElement } from '../components/ui/SharedElement';
import { useSharedElementId } from '../hooks/useSharedElementId';

interface Product {
  _id: string;
  name: string;
  price: number;
  image_url: string;
  description: string[];
  sort_id: number;
}

declare global {
  interface Window {
    turnstile: {
      render: (container: string | HTMLElement, options: any) => string;
      reset: (widgetId: string) => void;
    };
    onTurnstileSuccess?: (token: string) => void;
  }
}

export function ProductDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { setSharedElementId } = useSharedElement();
  const sharedElementId = useSharedElementId({ id });
  const [username, setUsername] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('QRIS');
  const [isBedrockEdition, setIsBedrockEdition] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileWidgetId, setTurnstileWidgetId] = useState<string | null>(null);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [isUsernameValid, setIsUsernameValid] = useState(false);

  const formatBedrockUsername = (name: string) => {
    return `be.${name.replace(/ /g, '_')}`;
  };

  const formatBedrockUsernameForDisplay = (name: string) => {
    return `.${name.replace(/ /g, '_')}`;
  };

  const validateUsername = (name: string, isBedrockEdition: boolean): { isValid: boolean; errorMessage: string | null } => {
    // Common validation for both editions
    if (!name.trim()) {
      return { isValid: false, errorMessage: t('store.errors.usernameRequired') };
    }

    if (isBedrockEdition) {
      // Bedrock Edition validation
      // Length: 3-20 characters (after trimming)
      if (name.trim().length < 3 || name.trim().length > 20) {
        return { isValid: false, errorMessage: t('store.errors.usernameLength', 'Username must be between 3 and 20 characters') };
      }
      
      // Bedrock allows spaces and some special characters
      // Only allow alphanumeric, spaces, and a limited set of special characters
      const bedrockRegex = /^[a-zA-Z0-9 ._]+$/;
      if (!bedrockRegex.test(name)) {
        return { isValid: false, errorMessage: t('store.errors.usernameInvalidChars', 'Username contains invalid characters') };
      }
    } else {
      // Java Edition validation
      // Length: 3-20 characters
      if (name.length < 3 || name.length > 20) {
        return { isValid: false, errorMessage: t('store.errors.usernameLength', 'Username must be between 3 and 20 characters') };
      }
      
      // Java Edition only allows alphanumeric, underscore, and hyphen
      const javaRegex = /^[a-zA-Z0-9_-]+$/;
      if (!javaRegex.test(name)) {
        return { isValid: false, errorMessage: t('store.errors.usernameInvalidChars', 'Username contains invalid characters') };
      }
    }

    return { isValid: true, errorMessage: null };
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await fetch(`${API_ENDPOINTS.store.products}/${id}`);
        if (!response.ok) {
          throw new Error('Failed to fetch product');
        }
        const data = await response.json();
        setProduct(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  useEffect(() => {
    let timeoutId: number;
    let retryCount = 0;
    const MAX_RETRIES = 5;
    const RETRY_DELAY = 2000; // 2 seconds between retries

    // Define the global callback function
    window.onTurnstileSuccess = (token: string) => {
      setTurnstileToken(token);
      setError(null);
    };

    // Initialize Turnstile widget when the script is ready
    const initTurnstile = () => {
      try {
        if (window.turnstile) {
          // Clear any existing widget
          if (turnstileWidgetId) {
            window.turnstile.reset(turnstileWidgetId);
          }

          const widgetId = window.turnstile.render('#turnstile-widget', {
            sitekey: import.meta.env.VITE_TURNSTILE_SITE_KEY,
            callback: window.onTurnstileSuccess,
            theme: 'dark',
            'refresh-expired': 'auto',
            'error-callback': () => {
              setError('Security check failed. Please try again.');
              setTurnstileToken(null);
            },
            'expired-callback': () => {
              setError('Security check expired. Please complete it again.');
              setTurnstileToken(null);
            }
          });
          setTurnstileWidgetId(widgetId);
          setError(null);
          return true;
        }
        return false;
      } catch (err) {
        console.error('Turnstile initialization error:', err);
        return false;
      }
    };

    const attemptInitialization = () => {
      if (retryCount >= MAX_RETRIES) {
        setError('Unable to load security check. Please refresh the page or try again later.');
        return;
      }

      if (!initTurnstile()) {
        retryCount++;
        timeoutId = window.setTimeout(attemptInitialization, RETRY_DELAY);
      }
    };

    // Start initialization attempts
    attemptInitialization();

    return () => {
      // Clean up timer
      if (timeoutId) window.clearTimeout(timeoutId);
      
      // Clean up the global callback and widget
      delete window.onTurnstileSuccess;
      if (turnstileWidgetId && window.turnstile) {
        window.turnstile.reset(turnstileWidgetId);
      }
    };
  }, []); // Only run once when component mounts

  useEffect(() => {
    let debounceTimeout: number;
    
    if (username.trim()) {
      // Only validate if the user has typed at least 2 characters
      if (username.trim().length >= 2) {
        // Debounce the validation to avoid showing errors while typing
        debounceTimeout = window.setTimeout(() => {
          const validation = validateUsername(username, isBedrockEdition);
          setUsernameError(validation.errorMessage);
          setIsUsernameValid(validation.isValid);
        }, 500); // 500ms delay
      } else {
        // Clear error when less than 2 characters
        setUsernameError(null);
        setIsUsernameValid(false);
      }
    } else {
      setUsernameError(null);
      setIsUsernameValid(false);
    }
    
    // Clean up timeout on component unmount or when dependencies change
    return () => {
      if (debounceTimeout) {
        window.clearTimeout(debounceTimeout);
      }
    };
  }, [username, isBedrockEdition]);

  const handleCheckout = async () => {
    // No need to validate username again since the button will be disabled if it's invalid
    if (!turnstileToken) {
      setError(t('store.errors.securityCheckRequired'));
      return;
    }

    setIsCheckingOut(true);
    setError(null);

    try {
      const finalUsername = isBedrockEdition ? formatBedrockUsername(username) : username;
      const response = await fetch(API_ENDPOINTS.store.createTransaction, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productId: id,
          username: finalUsername,
          paymentMethod,
          turnstileToken
        }),
      });

      if (!response.ok) {
        throw new Error(t('store.errors.transactionFailed'));
      }

      const data = await response.json();
      window.location.href = data.checkoutUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : t('store.errors.purchaseFailed'));
      if (turnstileWidgetId && window.turnstile) {
        window.turnstile.reset(turnstileWidgetId);
        setTurnstileToken(null);
      }
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handleBack = () => {
    if (id) {
      setSharedElementId(id);
    }
    const currentLanguage = i18n.language;
    navigate(`/${currentLanguage}/store`);
  };

  if (loading) {
    return (
      <PageTransition>
        <div className="min-h-screen bg-gray-900 py-24 flex justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
        </div>
      </PageTransition>
    );
  }

  if (error || !product) {
    return (
      <PageTransition>
        <div className="min-h-screen bg-gray-900 py-24 flex flex-col items-center">
          <div className="text-red-500 text-center mb-4">
            {error || 'Product not found'}
          </div>
          <Button onClick={() => navigate('/store')}>
            {t('store.backToStore', 'Back to Store')}
          </Button>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <SEO
        title={`${product.name} - ${t('store.title', 'Premium Points Store')}`}
        description={product.description[0]}
      />
      <div className="min-h-screen bg-gray-900">
        <div className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto">
            <div className="max-w-5xl mx-auto">
              <AnimatedSection animation="fade-up">
                {/* Back button */}
                <button 
                  onClick={handleBack}
                  className="flex items-center text-gray-400 hover:text-white mb-8 group transition-colors duration-300"
                >
                  <ArrowLeft className="mr-2 h-5 w-5 transform transition-transform duration-300 group-hover:-translate-x-1" />
                  {t('store.backToStore', 'Back to Store')}
                </button>

                {/* Main content */}
                <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-700/50">
                  <div className="p-8">
                    {/* Product header */}
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8">
                      <div>
                        <SharedElement 
                          id={`product-name-${id}`}
                          type="h1"
                          className="text-4xl font-bold text-white mb-3"
                          onAnimationComplete={() => setSharedElementId(null)}
                        >
                          {product?.name}
                        </SharedElement>
                        <div className="flex items-center space-x-2">
                          <div className="px-4 py-2 bg-primary-500/20 rounded-lg border border-primary-500/30">
                            <p className="text-primary-400 text-xl font-bold">
                              Rp{product?.price?.toLocaleString('id-ID')}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="mt-6 md:mt-0">
                        <div className="w-64 relative" style={{ aspectRatio: '2/1' }}>
                          <div className="absolute inset-0 overflow-hidden rounded-lg">
                            <SharedElement
                              id={`product-image-${id}`}
                              type="img"
                              src={product?.image_url}
                              alt={product?.name}
                              className="w-full h-full object-cover object-center"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Product description */}
                    <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 mb-8 border border-gray-700/50">
                      <h2 className="text-lg font-semibold text-white mb-4">
                        {t('store.description', 'Description')}
                      </h2>
                      <div className="space-y-4">
                        {product.description.map((desc, index) => (
                          <div key={index} className="flex items-start">
                            <div className="flex-shrink-0 w-1.5 h-1.5 mt-2 rounded-full bg-primary-500/50 mr-3" />
                            <p className="text-gray-300">
                              {desc}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Checkout form */}
                    <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
                      <h2 className="text-xl font-semibold text-white mb-6">
                        {t('store.checkoutButton')}
                      </h2>
                      <div className="space-y-6">
                        <div>
                          <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-2">
                            {t('store.username', 'Minecraft Username')}
                          </label>
                          <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0">
                            <div className="flex-1">
                              <input
                                type="text"
                                id="username"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                className={`w-full px-4 py-3 bg-gray-800/80 border ${usernameError ? 'border-red-500' : 'border-gray-600'} rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300`}
                                placeholder={t('store.usernamePlaceholder', 'Enter your Minecraft username')}
                              />
                              {usernameError && (
                                <p className="mt-2 text-sm text-red-500">
                                  {usernameError}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center">
                              <label className="relative inline-flex items-center cursor-pointer group">
                                <input
                                  type="checkbox"
                                  id="bedrockToggle"
                                  checked={isBedrockEdition}
                                  onChange={(e) => setIsBedrockEdition(e.target.checked)}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
                                <span className="ml-3 text-sm font-medium text-gray-300 group-hover:text-white transition-colors duration-200">
                                  Bedrock Edition
                                </span>
                              </label>
                            </div>
                          </div>
                          {isBedrockEdition && username && (
                            <p className="mt-3 text-sm text-gray-400">
                              {t('store.usernameWillBeSentAs')} <span className="text-primary-500 font-medium">{formatBedrockUsernameForDisplay(username)}</span>
                            </p>
                          )}
                        </div>

                        <div>
                          <label htmlFor="payment" className="block text-sm font-medium text-gray-300 mb-2">
                            {t('store.paymentMethod', 'Payment Method')}
                          </label>
                          <div className="relative">
                            <select
                              id="payment"
                              value={paymentMethod}
                              onChange={(e) => setPaymentMethod(e.target.value)}
                              className="w-full px-4 py-3 bg-gray-800/80 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 appearance-none"
                            >
                              <optgroup label="E-wallets" className="bg-gray-800 text-white">
                                <option value="QRIS" className="py-2">QRIS - Bayar dengan GoPay, OVO, Dana, dan lain-lain</option>
                              </optgroup>
                              <optgroup label="Virtual Accounts" className="bg-gray-800 text-white">
                                <option value="BRIVA" className="py-2">BRI Virtual Account - Transfer dari Bank BRI</option>
                                <option value="BNIVA" className="py-2">BNI Virtual Account - Transfer dari Bank BNI</option>
                                <option value="MANDIRIVA" className="py-2">Mandiri Virtual Account - Transfer dari Bank Mandiri</option>
                                <option value="BCAVA" className="py-2">BCA Virtual Account - Transfer dari Bank BCA (Biaya tambahan Rp5.500)</option>
                              </optgroup>
                              <optgroup label="Retail Stores" className="bg-gray-800 text-white">
                                <option value="ALFAMART" className="py-2">Alfamart - Bayar di gerai Alfamart (Biaya tambahan Rp3.500 di kasir)</option>
                                <option value="INDOMARET" className="py-2">Indomaret - Bayar di gerai Indomaret (Biaya tambahan Rp3.500 di kasir)</option>
                                <option value="ALFAMIDI" className="py-2">Alfamidi - Bayar di gerai Alfamidi (Biaya tambahan Rp3.500 di kasir)</option>
                              </optgroup>
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400">
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </div>
                        </div>

                        {error && (
                          <div className="text-red-500 text-sm p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                            {error}
                          </div>
                        )}

                        <div className="flex justify-start">
                          <div 
                            id="turnstile-widget"
                            className="cf-turnstile" 
                          ></div>
                        </div>

                        <div className="relative">
                          <Button
                            onClick={handleCheckout}
                            disabled={!turnstileToken || !isUsernameValid || isCheckingOut}
                            className="w-full py-4 text-lg font-semibold rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-primary-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={
                              !turnstileToken
                                ? t('store.errors.securityCheckRequired')
                                : !isUsernameValid
                                ? usernameError || t('store.errors.usernameRequired')
                                : ''
                            }
                          >
                            {isCheckingOut ? (
                              <div className="flex items-center justify-center space-x-2">
                                <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin" />
                                <span>{t('store.processingCheckout', 'Processing...')}</span>
                              </div>
                            ) : (
                              t('store.checkoutButton')
                            )}
                          </Button>
                        </div>
                        
                        <div className="mt-8 text-left">
                          <p className="text-gray-400 text-sm">
                            {t('store.trouble')} {" "}
                            <a 
                              href="https://trakteer.id/kaizenmc" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-primary-400 hover:text-primary-300 underline"
                            >
                              Trakteer
                            </a>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </PageTransition>
  );
} 